/**
 * Client for the oxMinter API.
 *
 * The console works completely without this. Signing in adds record sync, a
 * shared team view and an audit trail; it does not gate the wallet, and it is
 * never in the path of a transaction. Every call here is best-effort: if the
 * API is unset, unreachable, or the person is signed out, the console carries
 * on exactly as it did before, because losing an audit write must never stop a
 * launch that is already half-done on chain.
 *
 * `VITE_API_URL` empty (the default) disables the whole thing at build time.
 */

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export const apiConfigured = BASE.length > 0;

export interface Account {
  authenticated: boolean;
  user?: { id: string; email: string | null; name: string | null };
  org?: { id: string; name: string | null; role: "owner" | "admin" | "member" | "viewer" };
}

export interface CloudRecordSummary {
  id: string;
  created_at: number;
  cluster: string;
  symbol: string;
  mint: string;
  by_email: string | null;
  by_name: string | null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T | null> {
  if (!apiConfigured) return null;
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      // The session is an HttpOnly cookie; nothing readable by script ever
      // holds it, which is the point.
      credentials: "include",
      headers: {
        // Forces a CORS preflight, which a cross-site form or image cannot
        // satisfy. The API refuses state-changing requests without it.
        "X-OxMinter-Client": "1",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; status: number; detail: string };

/**
 * Like `call`, but it TELLS YOU WHY IT FAILED.
 *
 * `call` swallows everything and returns null on purpose: every existing use is
 * a best-effort audit or sync write, and a failed audit write must never
 * surface as a failed launch (invariant 13). Image generation is the opposite
 * kind of call -- the operator pressed a button and is waiting -- and the
 * reasons it can fail are all things they can act on: not signed in, daily cap
 * reached, no AI binding on this deployment. Collapsing those to null would
 * produce a spinner that stops with no explanation, which is the exact failure
 * mode this project keeps having to fix. `call` is left alone.
 */
async function callVerbose<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  if (!apiConfigured) return { ok: false, status: 0, detail: "This build has no API configured." };
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "X-OxMinter-Client": "1",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch (e) {
    return { ok: false, status: 0, detail: e instanceof Error ? e.message : "The API could not be reached." };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* a non-JSON body is reported by status alone */
  }
  if (!res.ok) {
    /*
        The field is `error`. api/src/http.ts: `problem()` writes
        `json({ error: message }, ...)`. Reading `detail` here would have
        silently degraded every real message -- "Daily limit of 40 generated
        images reached" -- into "The API answered 429", which is the generic
        failure this whole function exists to avoid.
    */
    const fromBody =
      body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string"
        ? (body as { error: string }).error
        : null;
    const detail =
      fromBody ??
      (res.status === 401 ? "Sign in to generate images." : `The API answered ${res.status}.`);
    return { ok: false, status: res.status, detail };
  }
  return { ok: true, data: body as T };
}

export interface GeneratedImage {
  /** Base64. The model returns JPEG whatever is asked for; `mime` says so. */
  image: string;
  mime: string;
  model: string;
  steps: number;
  remaining: number;
}

export interface ImageQuota {
  configured: boolean;
  limit: number;
  used: number;
  remaining: number;
  day: string;
}

/** Null when there is no API or no session; the caller then hides the panel. */
export async function imageQuota(): Promise<ImageQuota | null> {
  return await call<ImageQuota>("/image/quota");
}

export async function generateCoinImage(prompt: string): Promise<ApiResult<GeneratedImage>> {
  return await callVerbose<GeneratedImage>("/image/generate", {
    method: "POST",
    body: JSON.stringify({ prompt }),
  });
}

export function signInUrl(provider: string, redirectTo = "/"): string {
  return `${BASE}/auth/${provider}/start?redirect_to=${encodeURIComponent(redirectTo)}`;
}

export interface AuthOptions {
  providers: { id: string; label: string }[];
  /** True only when this deployment can actually send a verification link. */
  password: boolean;
  turnstileSiteKey: string | null;
}

/**
 * What this deployment can offer. `password` is false when no mail provider is
 * configured, because a signup that cannot send its confirmation link is an
 * account nobody can ever use -- better to hide the form than to offer a dead
 * end.
 */
export async function authOptions(): Promise<AuthOptions> {
  const r = await call<AuthOptions>("/auth/providers");
  return r ?? { providers: [], password: false, turnstileSiteKey: null };
}

/* Credential calls all use callVerbose: every way they fail is something the
   person can act on -- wrong password, unconfirmed address, rate limited, link
   expired -- and collapsing those to null would leave a spinner that stops
   with no explanation. */

export async function signUp(email: string, password: string, turnstile?: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/password/signup", {
    method: "POST",
    body: JSON.stringify({ email, password, ...(turnstile ? { turnstile } : {}) }),
  });
}

export async function signIn(email: string, password: string) {
  return callVerbose<{ ok: true }>("/auth/password/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function forgotPassword(email: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export async function resendVerification(email: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/verify/resend", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function changePassword(current: string, next: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/password/change", {
    method: "POST",
    body: JSON.stringify({ current, next }),
  });
}

export async function changeEmail(email: string, password: string) {
  return callVerbose<{ ok: true; detail: string }>("/auth/email/change", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function listProviders(): Promise<{ id: string; label: string }[]> {
  const r = await call<{ providers: { id: string; label: string }[] }>("/auth/providers");
  return r?.providers ?? [];
}

export async function whoami(): Promise<Account> {
  const r = await call<Account>("/me");
  return r ?? { authenticated: false };
}

export async function signOut(): Promise<void> {
  await call("/auth/logout", { method: "POST" });
}

export async function listCloudRecords(): Promise<CloudRecordSummary[]> {
  const r = await call<{ records: CloudRecordSummary[] }>("/records");
  return r?.records ?? [];
}

export async function getCloudRecord(id: string): Promise<unknown | null> {
  return call<unknown>(`/records/${encodeURIComponent(id)}`);
}

export async function saveCloudRecord(record: unknown): Promise<string | null> {
  const r = await call<{ id: string }>("/records", { method: "POST", body: JSON.stringify(record) });
  return r?.id ?? null;
}

export type AuditAction = "launch" | "burn" | "distribute" | "lp_lock" | "lp_burn" | "revoke";

/**
 * Record an irreversible action. Deliberately fire-and-forget: a failed audit
 * write must never surface as a launch failure, and the signature it carries
 * can be checked against the chain by anyone who does not trust this database.
 */
export function recordEvent(event: {
  action: AuditAction;
  cluster: string;
  mint?: string;
  pool_id?: string;
  signature?: string;
  wallet?: string;
  detail?: string;
}): void {
  if (!apiConfigured) return;
  void call("/events", { method: "POST", body: JSON.stringify(event) });
}

export interface AlertResult {
  recorded: boolean;
  emailed: boolean;
  to?: string;
  via?: string;
  reason?: string;
}

/**
 * Ask the API to email the operator about a distribution that did not go
 * perfectly. Returns what actually happened rather than a boolean, so the
 * console can say "recorded, but not emailed because no provider is
 * configured" instead of showing a tick over nothing.
 */
export async function sendDistributionAlert(report: unknown): Promise<AlertResult | null> {
  return call<AlertResult>("/alerts/distribution", {
    method: "POST",
    body: JSON.stringify(report),
  });
}
